import { geoMercator, geoPath } from 'd3-geo';
import { scaleLinear, scaleThreshold } from 'd3-scale';
import { event, select } from 'd3-selection';
import 'd3-transition';
import { Feature, FeatureCollection, MultiPolygon } from 'geojson';
import { debounce, delay, isEqual, max, sum, uniqBy } from 'lodash';
import * as React from 'react';
import * as ReactTooltip from 'react-tooltip';
import * as topojson from 'topojson-client';
import { Topology } from 'topojson-specification';
import { TownshipData } from '../store';

// tslint:disable:no-any
// tslint:disable:only-arrow-functions

const stopPropagation = () => {
  if (event && event.stopPropagation) {
    event.stopPropagation();
  }
}

const noOpFunc = () => {
  stopPropagation();
};

const stateLookupData: { [code: string]: string } = {
  // Bago
  'MMR007': 'MMR111',
  'MMR008': 'MMR111',
  // Shan
  'MMR014': 'MMR222',
  'MMR015': 'MMR222',
  'MMR016': 'MMR222',
}

const stateLookup = (code: string) => {
  if (stateLookupData[code]) {
    return stateLookupData[code];
  }

  return code;
}

interface P {
  data: TownshipData[];
  size: [number, number];
  selectedState?: string;
  onStateSelect?: (code: string) => () => void;
  onTownshipSelect?: (code: string) => () => void;
  onUnselectFunc?: () => void;
}

interface S {
  selectedState?: Feature;
}
export default class extends React.Component<P, S> {
  gnode?: SVGGElement;
  my?: Topology;
  stateColors: (n: number) => string;
  animation: boolean;

  constructor(props: P) {
    super(props);

    this.state = {};
    this.stateColors = _ => '#fff';
    this.animation = false;

    this.handleSVGClick = debounce(this.handleSVGClick, 1000, { leading: true });
  }

  componentDidMount() {
    this.createMap();
  }

  getMyanmarGeo = async () => {
    if (!this.my) {
      const resp = await fetch('/maps/my-townships.geo.json');
      this.my = await resp.json();
    }

    return this.my as Topology;
  }

  heatColor = (maxNumber: number) => (count: number) => {
    const colorRange = [
      '#FFFFFF',
      '#BCE4D8',
      '#B4DCD3',
      '#ACD5CF',
      '#A5CECA',
      '#9DC6C6',
      '#96BFC2',
      '#8EB8BD',
      '#86B0B9',
      '#7FA9B5',
      '#77A2B0',
      '#709AAC',
      '#6893A7',
      '#618CA3',
      '#59849F',
      '#517D9A'
    ];

    const colors: (n: number) => string = scaleThreshold<number, string>()
      .domain([0, maxNumber * 0.01, maxNumber * 0.02, maxNumber * 0.03,
        maxNumber * 0.04, maxNumber * 0.05,
        maxNumber * 0.1, maxNumber * 0.2, maxNumber * 0.3,
        maxNumber * 0.4, maxNumber * 0.5, maxNumber * 0.6,
        maxNumber * 0.7, maxNumber * 0.8, maxNumber])
      .range(colorRange);

    return colors(count);
  }

  componentWillReceiveProps(nextProps: P) {
    if (!isEqual(this.props, nextProps)) {
      if (this.props.data.length > 0 && nextProps.data.length > 0) {
        this.updateMap(nextProps.data);
      } else {
        this.createMap();
      }
    }
  }

  async updateMap(townships: TownshipData[]) {
    if (this.gnode && this.props.selectedState) {
      const { size: [width, height] } = this.props;
      const my = await this.getMyanmarGeo();
      const features = topojson.feature(my, my.objects.townships) as FeatureCollection;
      const projection = geoMercator().fitSize([width, height], features);
      const path = geoPath().projection(projection);
      const stateTownships = features
        .features
        .filter(f => f.properties && stateLookup(f.properties.st_pcode) === this.props.selectedState)
        .map(f => ({
          feature: f,
          title: this.getTitle({ township: f.properties && f.properties.ts_pcode }),
          data: townships.find(inv => inv.townshipCode === (f.properties && f.properties.ts_pcode)),
        }));

      const g = select(this.gnode);
      g.select('g.townships').remove();
      const map =
        g.append('g')
          .attr('class', 'townships')
          .selectAll('path')
          .data(stateTownships);

      const townshipMax = max(stateTownships.map(inv => inv.data ? inv.data.capital : 0)) || 0;
      const townshipColors = scaleLinear<string>()
        .domain([0, townshipMax])
        .range(['#ffffff', '#517d9a']);
      map
        .enter()
        .append('path')
        .attr('id', td => td.feature.properties && td.feature.properties.ts_pcode)
        .attr('class', 'township')
        .attr("data-tip", ({ title }) => title)
        .attr('d', td => path(td.feature))
        .transition()
        .duration(300)
        .style("fill", ({ data: tdata, feature }) =>
          townshipColors(tdata ? tdata.capital : 0));

      ReactTooltip.rebuild();
    }
  }

  getTitle = ({ state, township }: { state?: string, township?: string }) => {
    const { data } = this.props;
    if (township) {
      const tns = data.find(inv => inv.townshipCode === township);
      if (tns) {
        return `${tns.township} - $${Math.round(tns.capital)}MM`;
      }
    }

    if (state) {
      const tns = data.find(inv => inv.stateCode === state);
      if (tns) {
        return `${tns.state} - $${Math.round(tns.capital)}MM`;
      }
    }

    return '';
  }

  zoomOut = () => {
    if (!this.gnode) {
      return;
    }

    const { onUnselectFunc } = this.props;
    const g = select(this.gnode);

    g
      .select('g.townships')
      .selectAll('path')
      .transition()
      .duration(750)
      .style('fill', '#ffffff');

    this.animation = true;

    // wait for animation
    delay(() => {
      g.select('g.townships').remove();

      g.select('g#states')
        .selectAll('path')
        .style('stroke', '#75888b')
        .style("fill", ({ count }) => this.stateColors(count));

      if (onUnselectFunc) {
        onUnselectFunc();
      }

      this.animation = false;
    }, 750);

    stopPropagation();
  }

  createMap = async () => {
    if (this.gnode) {
      const { size: [width, height], selectedState, onStateSelect, onTownshipSelect, data } = this.props;

      const my = await this.getMyanmarGeo();
      const features = topojson.feature(my, my.objects.townships) as FeatureCollection;
      const g = select(this.gnode);

      const projection = geoMercator().fitSize([width, height], features);
      const path = geoPath().projection(projection);

      const states = uniqBy(features.features, f => f.properties && stateLookup(f.properties.st_pcode))
        .map(f => f.properties && stateLookup(f.properties.st_pcode))
        .map(c => ({
          st_pcode: stateLookup(c || ''),
          title: this.getTitle({ state: stateLookup(c || '') }),
          count: sum(data.filter(inv => inv.stateCode === (c || '')).map(inv => inv.capital)) || 0,
          d: topojson.merge(
            my,
            (my.objects.townships as any)
              .geometries
              .filter((d: any) =>
                stateLookup(d.properties.st_pcode) === c &&
                // d.properties.ts_pcode !== 'MMR015D007' &&
                d.properties.ts_pcode !== 'MMR013032')
          )
        }));

      const stateMax = max(states.map(std => std.count)) || 0;
      this.stateColors = stateMax ? this.heatColor(stateMax) : (_: number) => '#fff';

      let centered: any;
      const clicked = (d: { st_pcode: string, d: MultiPolygon }) => {
        if (this.animation) {
          return;
        }

        // tslint:disable-next-line:one-variable-per-declaration
        let x, y, k;

        if (d && !centered) {
          const centroid = path.centroid(d.d);
          x = centroid[0];
          y = centroid[1];
          // calculate zoom level
          const bounds = path.bounds(d.d);
          const dx = bounds[1][0] - bounds[0][0];
          const dy = bounds[1][1] - bounds[0][1];
          k = 0.9 / Math.max(dx / width, dy / height);
          centered = d.d;
        } else {
          x = width / 2;
          y = height / 2;
          k = 1;
          centered = null;
        }

        g.selectAll("path")
          .classed("active", centered && function (d2: any) { return d2 === centered; });

        g.transition()
          .duration(750)
          .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")scale(" + k + ")translate(" + -x + "," + -y + ")")
          .style("stroke-width", 1 / k + "px");

        if (centered && onStateSelect) {
          // tslint:disable-next-line:no-console
          console.log('clicked on: ', centered);

          const stateTownships = (topojson.feature(my, my.objects.townships) as FeatureCollection)
            .features
            .filter(f => f.properties && stateLookup(f.properties.st_pcode) === d.st_pcode)
            .map(f => ({
              feature: f,
              title: this.getTitle({ township: f.properties && f.properties.ts_pcode }),
              data: data.find(inv => inv.townshipCode === (f.properties && f.properties.ts_pcode)),
            }));

          const townshipMax = max(stateTownships.map(inv => inv.data ? inv.data.capital : 0)) || 0;
          const townshipColors = scaleLinear<string>()
            .domain([0, townshipMax])
            .range(['#ffffff', '#517d9a']);

          let selectedTownship: string = ''
          const townshipClicked = (f: Feature) => {
            if (onTownshipSelect && f.properties !== null) {
              g.select('g.townships')
                .selectAll('path.township')
                .style("fill", ({ data: tdata, feature }) =>
                  feature === f ?
                    f.properties && f.properties.ts_pcode !== selectedTownship ?
                      '#ff7f0e' : townshipColors(tdata ? tdata.capital : 0) :
                    townshipColors(tdata ? tdata.capital : 0));

              if (f.properties && f.properties.ts_pcode !== selectedTownship) {
                delay(() => onTownshipSelect(f.properties && f.properties.ts_pcode)(), 500);
                selectedTownship = f.properties ? f.properties.ts_pcode : '';
              } else {
                selectedTownship = '';
                delay(() => onStateSelect(d.st_pcode)(), 500);
              }

              stopPropagation();
            }
          };

          g.select('g#states')
            .selectAll('path')
            .style('stroke', '#d5dbdc')
            .style('fill', '#fff');

          g.append('g')
            .attr('class', 'townships')
            .selectAll('path')
            .data(stateTownships)
            .enter()
            .append('path')
            .attr('id', td => td.feature.properties && td.feature.properties.ts_pcode)
            .attr('class', 'township')
            .attr("data-tip", ({ title }) => title)
            .attr('d', td => path(td.feature))
            .on('click', td => townshipClicked(td.feature));

          g.select('g.townships')
            .selectAll('path.township')
            .transition()
            .duration(750)
            .style("fill", ({ data: tdata }) => townshipColors(tdata ? tdata.capital : 0));
          delay(() => onStateSelect(d.st_pcode)(), 750);
        }

        if (!centered) {
          this.zoomOut();
        }

        ReactTooltip.rebuild();
        stopPropagation();
      }

      g.append("g")
        .attr("id", "states")
        .selectAll(".state")
        .append("path")
        .data(states)
        .enter()
        .append("path")
        .attr("class", "state")
        .attr("data-tip", ({ title }) => title)
        .attr("d", ({ d }) => path(d))
        .style("fill", ({ count }) => this.stateColors(count))
        .on("click", !selectedState ?
          clicked : noOpFunc);

      if (selectedState) {
        const st = states.find(s => s.st_pcode === selectedState);
        if (st) {
          delay(() => clicked(st), 300);
        }
      }

      ReactTooltip.rebuild();
    }
  }

  handleSVGClick = () => {
    if (this.gnode) {
      const g = select(this.gnode);
      const { size: [width, height] } = this.props;
      const x = width / 2;
      const y = height / 2;
      const k = 1;
      g.transition()
        .duration(750)
        .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")scale(" + k + ")translate(" + -x + "," + -y + ")")
        .style("stroke-width", 1 / k + "px");
    }
    this.zoomOut();
  }

  render() {
    const { size: [width, height], selectedState } = this.props;
    return (
      <React.Fragment>
        <ReactTooltip />
        <svg
          className="map"
          preserveAspectRatio="none"
          viewBox={`0 0 ${width} ${height}`}
          style={{ verticalAlign: 'top', marginTop: '10px' }}
          onClick={!selectedState ? this.handleSVGClick : noOpFunc}
        >
          <g
            ref={node => this.gnode = (node || undefined)}
          />
        </svg>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/MyanmarMap.tsx